import * as React from 'react'
import { useOrganizations } from '@repo/api/identity-service'
import { useOrganization as useDataOrganization } from '@repo/api/data-api'
import { baseQueryConfig, getClientApiErrorMessage } from '@/lib/sentry/client-errors'
import { longTermCacheConfig } from '@repo/api/utils'
import { CombinedOrganization } from '@/types'
import { useUser } from '@clerk/nextjs'

/**
 * This hook fetches all organizations once and caches them using long-term cache config.
 * Waits for authentication to be ready before making API calls to avoid 401 errors. TODO: broadly fix session race condition with identity API
 * TODO: revisit as organization count grows
 */
export const useStaticOrganizations = () => {
  const { isLoaded: clerkLoaded, isSignedIn } = useUser()

  const organizationsResult = useOrganizations(
    {},
    {
      ...baseQueryConfig,
      ...longTermCacheConfig,
      enabled: clerkLoaded && isSignedIn,
    }
  )
  const statsResult = useDataOrganization(
    { filter_set: { filters: [] } },
    {
      ...baseQueryConfig,
      ...longTermCacheConfig,
      enabled: clerkLoaded && isSignedIn,
    }
  )

  return {
    ...organizationsResult,
    statsData: statsResult.data,
    statsError: statsResult.error ? getClientApiErrorMessage(statsResult.error) : null,
    error: organizationsResult.error ? getClientApiErrorMessage(organizationsResult.error) : null,
  }
}

/**
 * Helper hook for getting a specific organization by ID
 * Uses the cached data from useStaticOrganizations
 */
export function useStaticOrganizationById(organizationId: string) {
  const { data: organizations, isLoading, error, isError } = useStaticOrganizations()

  const organization = organizations?.organizations?.find((org) => org.id === organizationId)

  return {
    organization,
    isLoading,
    error,
    isError,
  }
}

/**
 * Helper hook for getting organization statistics by ID
 * Uses the cached data from useStaticOrganizations
 */
export function useStaticOrganizationStats(organizationId: string) {
  const { statsData, isLoading, statsError } = useStaticOrganizations()

  const orgStats = statsData?.organization_stats?.[organizationId]

  return {
    stats: orgStats,
    isLoading,
    error: statsError,
    isError: !!statsError,
  }
}

/**
 * Helper hook for getting a specific organization with combined stats and details
 * Uses the same pattern as OrganizationGrid - combines Data API stats with Identity Service details
 */
export function useStaticOrganizationDetails(organizationId: string) {
  const { data: staticOrgsData, isLoading: staticLoading, error: staticError } = useStaticOrganizations()
  const { stats, isLoading: statsLoading, error: statsError } = useStaticOrganizationStats(organizationId)

  const staticOrgsMap = React.useMemo(() => {
    if (!staticOrgsData?.organizations) return new Map()
    return new Map(staticOrgsData.organizations.map((org) => [org.id, org]))
  }, [staticOrgsData?.organizations])

  const organization: CombinedOrganization = React.useMemo(() => {
    const staticOrg = staticOrgsMap.get(organizationId)

    return {
      id: organizationId,
      ...stats,
      ...staticOrg,
    }
  }, [stats, staticOrgsMap, organizationId])

  return {
    organization,
    isLoading: staticLoading || statsLoading,
    error: staticError || statsError,
    isError: !!staticError || !!statsError,
  }
}

/**
 * Helper hook for getting multiple organizations by IDs
 * Uses the Identity Service API directly for individual organizations
 * Note: This will make individual API calls for each organization ID
 */
export function useStaticOrganizationsByIds(organizationIds: string[]) {
  // For now, we'll use the cached organizations list approach
  // TODO: Consider implementing a batch API call if available
  const { data: organizations, isLoading, error, isError } = useStaticOrganizations()

  const foundOrganizations = organizationIds
    .map((id) => organizations?.organizations?.find((org: any) => org.id === id))
    .filter(Boolean)

  return {
    organizations: foundOrganizations,
    isLoading,
    error,
    isError,
  }
}
